/**
 * Series Grid Component.
 * @file Similar in implementation to `@swm/media-shelf` but simplified to only
 * render a grid of series cards. Because this is in the app and we have
 * control over the data going in, there's less defence against bad data.
 *
 * WARNING: Because this component currently contains a `Scroll`, there should
 * only ever be one instance of `SeriesGrid` per page.
 */
import { arr } from '@accedo/general-utils';
import { Nav, NavNode } from '@accedo/spatial-nav';
import { NavScroll, ScrollProps, VERTICAL } from '@accedo/virtual-scroll';
import { stylesheet } from 'astroturf';
import { ComponentChild, FunctionalComponent, h } from 'preact';
import { useMemo } from 'preact/hooks';
import { Link } from 'preact-router';
import { Image } from '@accedo/ctv-image';
import { PageNavigation } from '##/utils/PageNavigation';
import { reformatSubtitleStr } from '##/utils';

const styles = stylesheet`
  @import '##/styles/import.scss';

  .row {
    display: flex;
    justify-content: flex-start;
    margin-bottom: pxToVw(40);
  
  }

  .grid-outer-element {
    padding: pxToVw(16);
    padding-bottom: 0;
    overflow: hidden;
    height: 80%;
  }

  .card-container {
    flex-shrink: 0;
    margin-right: pxToVw(18);
    width: pxToVw(166);
  }
  .card-title {
    padding-top: pxToVw(18);
    font-family: barlow;
    font-size: 1.5rem;
    color: $primary-grey;
  }
  .card-subtitle {
    padding-top: pxToVw(10);
    font-family: barlow;
    font-size: 1.5rem;
    color: $primary-grey;
    white-space: nowrap;
    overflow-x: hidden;
    height: pxToVh(29);
    text-overflow: ellipsis;
  }

  .image {
    background-color: $primary-grey-30;
    object-fit: cover;
  }

  :global(.current-focus) {
    .card-title, .card-subtitle {
      color: $pure-white;
    }
    .image {
      box-shadow: 0 0 0 2px $pure-white;
      border-radius: 2px;
      transform: scale(1.1, 1.1);
    }
  }

`;
export type ChildItem = {
  id: string;
  imgUrl: string;
  fallbackUrl?: string;
  route: string;
  showApiEndpoint: string;
  imgHeight: number;
  imgWidth: number;
  navProps?: NavNode;
  subtitle1?: string;
  subtitle2?: string;
  beforeClickHandler?: () => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function makeGrid<T extends any[]>(items: T, colWidth: number): T[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gridItems: any[] = [];

  arr.forEach(items, (_item, index, array) => {
    if (index % colWidth === 0) {
      gridItems.push(array.slice(index, index + colWidth));
    }
  });

  return gridItems;
}

function rowMapper(item: ChildItem, colIndex: number): ComponentChild {
  const onCardClick = (): void => {
    if (item.beforeClickHandler) item.beforeClickHandler();
    PageNavigation.navigateTo(item.route, item.showApiEndpoint);
  };
  return (
    <div id={item.id} className={styles.cardContainer}>
      <Nav.Focusable id={item.id} index={colIndex} {...item.navProps} onSelect={onCardClick}>
        <Link onClick={onCardClick}>
          <Image
            className={styles.image}
            url={`${item.imgUrl}?image-profile=foxtel_poster`}
            fallbackUrl={item.fallbackUrl}
            width={item.imgWidth}
            height={item.imgHeight}
          />
          {item.subtitle1 ? (
            <div className={styles.cardTitle}>{item.subtitle1}</div>
          ) : null}
          {item.subtitle2 ? (
            <div className={styles.cardSubtitle}>
              {reformatSubtitleStr(item.subtitle2)}
            </div>
          ) : null}
        </Link>
      </Nav.Focusable>
    </div>
  );
}

function scaleImageHeight(px: number): number {
  const newScale = (100 * px) / 720;
  return newScale;
}

type gridProps = {
  index: number;
  rowId: string;
  isInbound?: boolean;
  items: ChildItem[];
};

function GridRails({
  index,
  rowId,
  items,
  isInbound,
}: gridProps): h.JSX.Element {
  const searchAssetsCheck = items[0].subtitle1 || items[0].subtitle2;
  return (
    <Nav.Horizontal index={index} id={rowId}>
      <div
        id={rowId}
        className={styles.row}
        style={{
          height: `${scaleImageHeight(searchAssetsCheck ? 315 : 251)}vh`,
        }}
      >
        {isInbound && arr.map(items, rowMapper)}
      </div>
    </Nav.Horizontal>
  );
}

function gridMapper(items: ChildItem[], rowIndex: number): ComponentChild {
  return <GridRails index={rowIndex} rowId={`row-${rowIndex}`} items={items} />;
}

export interface GridProps {
  id?: string;
  gridCols?: number;
  items: ChildItem[];
  navProps?: NavNode;
  lazyLoadBoundary?: number;
  scrollProps?: Partial<ScrollProps>;
  className: string;
  scrollEndBoundary: number;
}

export const Grid: FunctionalComponent<GridProps> = ({
  id,
  gridCols = 4,
  items,
  navProps,
  scrollProps,
  className,
  scrollEndBoundary,
  lazyLoadBoundary = 2,
}) => {
  const gridItems = useMemo<ChildItem[][]>(() => makeGrid(items, gridCols), [
    items,
  ]);

  return (
    <NavScroll
      id={id ?? ''}
      orientation={VERTICAL}
      scrollEndBoundary={scrollEndBoundary}
      className={className}
      {...scrollProps}
      navProps={{
        isIndexAlign: true,
        ...navProps,
      }}
      outerClassName={styles.gridOuterElement}
      lazyLoadBoundary={lazyLoadBoundary}
    >
      {arr.map(gridItems, gridMapper)}
    </NavScroll>
  );
};
